/**
 * @file Helper functions related to device uploads.
 */
import { DraftPost, PostAttributes, WishAttachment } from '@@/types'

/**
 * Checks if the attachment is possibly being uploaded from the Device
 * Uploads feature. These uploads generally have a file of size 0 because the
 * file isn't stored on the host, but instead is being uploaded from the client.
 * The latter will send some metadata about the upload so the host can emulate a
 * real upload in the UPE and display progress, but the file's content is not
 * available on the host.
 *
 * That being said, this is not a guarantee because in some very rare edge cases
 * the host could also upload a file of size 0, but even then broadcasting the
 * cancel event to the client wouldn't be a problem because the client will
 * ignore it.
 *
 * @param attachment - The attachment to check.
 * @returns `true` if the attachment is possibly being uploaded from the Device
 * Uploads feature.
 */
function isWishAttachmentPossiblyBeingUploadedFromDevice(attachment: WishAttachment): boolean {
  return attachment.fileBeingUploaded != null && attachment.fileBeingUploaded.size === 0
}

/**
 * Checks if the draft possibly contains an upload initiated by the Device
 * Uploads feature. These uploads generally have a file of size 0 because the
 * file isn't stored on the host, but instead is being uploaded from the client.
 * The latter will send some metadata about the upload so the host can emulate a
 * real upload in the UPE and display progress, but the file's content is not
 * available on the host.
 *
 * That being said, this is not a guarantee because in some very rare edge cases
 * the host could also upload a file of size 0, but even then broadcasting
 * events following these false positives to the client wouldn't be a problem
 * because the client will ignore them.
 *
 * @param draft - The draft to check.
 * @returns `true` if the draft possibly contains an upload initiated by the Device
 * Uploads feature.
 */
function draftPossiblyContainsUploadFromDevice(draft: DraftPost): boolean
function draftPossiblyContainsUploadFromDevice(draft: PostAttributes): boolean

function draftPossiblyContainsUploadFromDevice(draft: DraftPost | PostAttributes): boolean {
  return (
    ('uploadingFile' in draft && draft.uploadingFile != null && draft.uploadingFile.size === 0) ||
    (!!draft.attachments?.length && draft.attachments.some(isWishAttachmentPossiblyBeingUploadedFromDevice))
  )
}

export { draftPossiblyContainsUploadFromDevice, isWishAttachmentPossiblyBeingUploadedFromDevice }
